import { useEffect } from "react";
import { useLocation, useNavigate } from "@tanstack/react-router";
import { iapAvailable } from "@/lib/native-iap";
import {
  isCommerceRoute,
  isExternalPurchaseUrl,
  shouldHideCommerce,
  nativePlatform,
} from "@/lib/native-app";

/**
 * Inside the native app only: keep every "buy a membership" surface out of
 * sight, as Apple requires while we have no in-app purchase.
 *
 * Web visitors are never affected — every check below is gated on the app
 * shell being present.
 */
export function NativeCommerceGuard() {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  // Mark the document so CSS can hide commerce blocks marked data-commerce.
  useEffect(() => {
    if (!shouldHideCommerce()) return;
    const root = document.documentElement;
    root.setAttribute("data-native-app", nativePlatform());
    root.setAttribute("data-hide-commerce", "true");
    return () => {
      root.removeAttribute("data-hide-commerce");
    };
  }, []);

  // Selling pages simply do not exist in the app.
  useEffect(() => {
    if (!shouldHideCommerce()) return;
    if (isCommerceRoute(pathname)) {
      navigate({ to: iapAvailable() ? "/app-membership" : "/my-membership", replace: true });
    }
  }, [pathname, navigate]);

  // Any link that leads to a purchase (in-app route or Stripe/PayPal) is removed.
  useEffect(() => {
    if (!shouldHideCommerce()) return;

    const PRICE = /(?:[$£€]\s?\d|\d+(?:\.\d{2})?\s?(?:USD|GBP|EUR)\b)/;
    const BUY_WORDS =
      /^(join( as (a )?member| us)?$|join now|sign up (and|&) join|get membership|join today|join recovery trauma|subscribe|upgrade|become a member|start (my )?membership|choose (your )?plan|see (the )?pricing|pay now|continue to payment)/i;

    const hide = (el: HTMLElement) => {
      // The Apple plans screen is the one allowed place to buy in the app.
      if (el.closest("[data-iap]")) return;
      if (el.dataset.commerceScrubbed === "1") return;
      el.dataset.commerceScrubbed = "1";
      el.dataset.commercePreviousAriaHidden = el.getAttribute("aria-hidden") ?? "";
      el.setAttribute("aria-hidden", "true");
      el.style.display = "none";
    };

    const restore = (el: HTMLElement) => {
      if (el.dataset.commerceScrubbed !== "1") return;
      const previousAriaHidden = el.dataset.commercePreviousAriaHidden;
      if (previousAriaHidden) el.setAttribute("aria-hidden", previousAriaHidden);
      else el.removeAttribute("aria-hidden");
      el.style.removeProperty("display");
      delete el.dataset.commerceScrubbed;
      delete el.dataset.commercePreviousAriaHidden;
    };

    const scrub = () => {
      document.querySelectorAll<HTMLAnchorElement>("a[href]").forEach((a) => {
        const href = a.getAttribute("href") || "";
        if (!href || href.startsWith("#")) return;
        if (isExternalPurchaseUrl(href)) hide(a);
        else restore(a);
      });

      // Buttons and links whose label is an invitation to buy.
      document.querySelectorAll<HTMLElement>("a, button").forEach((el) => {
        const text = (el.textContent || "").trim();
        if (!text) return;
        if (BUY_WORDS.test(text) || PRICE.test(text)) hide(el);
        else if (el.tagName !== "A") restore(el);
      });

      // Any small block of text that shows a price.
      const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT);
      let node: Node | null = walker.nextNode();
      while (node) {
        const text = node.nodeValue || "";
        if (PRICE.test(text)) {
          const parent = node.parentElement;
          if (parent && !["BODY", "MAIN", "HTML"].includes(parent.tagName)) hide(parent);
        }
        node = walker.nextNode();
      }
    };

    scrub();
    const observer = new MutationObserver(scrub);
    observer.observe(document.body, { childList: true, subtree: true });

    // Belt and braces: block programmatic navigation to external checkout.
    const openBlocker = (event: MouseEvent) => {
      const target = (event.target as HTMLElement | null)?.closest?.("a[href]") as HTMLAnchorElement | null;
      if (target && isExternalPurchaseUrl(target.getAttribute("href") || "")) {
        event.preventDefault();
        event.stopPropagation();
      }
    };
    document.addEventListener("click", openBlocker, true);

    return () => {
      observer.disconnect();
      document.removeEventListener("click", openBlocker, true);
    };
  }, []);

  return null;
}
